import ChartHeader from '../ChartHeader';
import NoResponsesAvailableYet from '../NoResponsesAvailableYet';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Box, Typography, FormGroup, FormControlLabel, Checkbox, Paper } from '@mui/material';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

//Categorical BM - Carbon. See https://www.nudgebi.com/color-library
const categoricalColors = [
  '#6929c4',
  '#1192e8',
  '#005d5d',
  '#9f1853',
  '#fa4d56',
  '#570408',
  '#198038',
  '#002d9c',
  '#ee538b',
  '#b28600',
  '#009d9a',
  '#012749',
  '#8a3800',
  '#a56eff'
];

const CompletionOverTimeChart = ({ data, onRefresh, isFirstLoad }) => {
  const chartRef = useRef(null);
  const [visibleYearLevels, setVisibleYearLevels] = useState(new Set());
  const [chartInstance, setChartInstance] = useState(null);
  const [yearLevelColors, setYearLevelColors] = useState({});
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    if (onRefresh) {
      await onRefresh();
    }
    setTimeout(() => setIsManualRefreshing(false), 500);
  };

  // Initialize visible year levels and assign colors
  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      const yearLevels = Object.keys(data);
      setVisibleYearLevels(new Set(yearLevels));

      // Assign colors in order
      const colorMapping = {};
      yearLevels.forEach((yearLevel, index) => {
        colorMapping[yearLevel] = categoricalColors[index % categoricalColors.length];
      });
      setYearLevelColors(colorMapping);
    }
  }, [data]);

  const handleYearLevelToggle = (yearLevel) => {
    const newVisibleYearLevels = new Set(visibleYearLevels);
    if (newVisibleYearLevels.has(yearLevel)) {
      newVisibleYearLevels.delete(yearLevel);
    } else {
      newVisibleYearLevels.add(yearLevel);
    }
    setVisibleYearLevels(newVisibleYearLevels);
  };

  useLayoutEffect(() => {
    if (!data || Object.keys(data).length === 0 || Object.keys(yearLevelColors).length === 0) return;

    // Create root
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove logo
    root._logo.dispose();

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: root.verticalLayout
      })
    );

    // Add cursor
    const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
    cursor.lineY.set('visible', false);

    // Create axes
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxZoomCount: 1000,
        baseInterval: {
          timeUnit: 'day',
          count: 1
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, {
          strokeDasharray: []
        })
      })
    );

    // Add X-axis title (Date) - positioned below the axis
    xAxis.children.push(
      am5.Label.new(root, {
        text: 'Date',
        x: am5.p50,
        centerX: am5.p50,
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color('#666666'),
        paddingTop: 15
      })
    );

    // Add Y-axis title (Number of responses)
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Number of responses',
        rotation: -90,
        y: am5.p50,
        centerX: am5.p50,
        fontSize: 14,
        fontWeight: 'bold',
        fill: am5.color('#666666'),
        paddingRight: 15
      })
    );

    // Hide all grid lines
    yAxis.get('renderer').grid.template.set('forceHidden', true);
    xAxis.get('renderer').grid.template.set('forceHidden', true);

    // Ensure axis baselines are visible and styled
    yAxis.get('renderer').set('strokeOpacity', 1);
    yAxis.get('renderer').set('stroke', am5.color('#dedede'));
    yAxis.get('renderer').set('strokeWidth', 1);

    xAxis.get('renderer').set('strokeOpacity', 1);
    xAxis.get('renderer').set('stroke', am5.color('#dedede'));
    xAxis.get('renderer').set('strokeWidth', 1);

    // Store series references
    const seriesMap = new Map();

    // Create series for each year level
    Object.keys(yearLevelColors).forEach((yearLevel) => {
      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: yearLevel,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'cumulativeCount',
          valueXField: 'date',
          categoryXField: 'date',
          tooltip: am5.Tooltip.new(root, {
            pointerOrientation: 'horizontal',
            labelText: '{name}: {valueY} responses'
          })
        })
      );

      // Force set the stroke color immediately after series creation
      const lineColor = am5.color(yearLevelColors[yearLevel]);

      series.set('stroke', lineColor);

      // Also set it on the template for consistency
      series.strokes.template.setAll({
        strokeWidth: 3,
        stroke: lineColor
      });

      // Set the fill color as well
      series.set('fill', lineColor);

      series.fills.template.setAll({
        fillOpacity: 0,
        visible: true,
        fill: lineColor
      });

      seriesMap.set(yearLevel, series);
    });

    // Prepare and set data
    const updateChartData = () => {
      Object.keys(yearLevelColors).forEach((yearLevel) => {
        const series = seriesMap.get(yearLevel);
        const yearData = data[yearLevel] || [];

        const chartData = yearData.map((item) => ({
          date: new Date(item.date).getTime(),
          cumulativeCount: item.cumulativeCount,
          cumulativePercentage: item.cumulativePercentage
        }));

        series.data.setAll(chartData);

        // Show/hide series based on visibility
        if (visibleYearLevels.has(yearLevel)) {
          series.show();
        } else {
          series.hide();
        }
      });
    };

    updateChartData();
    setChartInstance({ root, chart, seriesMap });

    if (isFirstLoad) {
      // Make stuff animate on load - AFTER data is set
      Object.keys(yearLevelColors).forEach((yearLevel) => {
        const series = seriesMap.get(yearLevel);
        if (series && visibleYearLevels.has(yearLevel)) {
          series.appear(1000);
        }
      });
      chart.appear(1000, 100);
    }

    return () => {
      root.dispose();
    };
  }, [data, yearLevelColors]);

  // Update visibility when visibleYearLevels changes
  useLayoutEffect(() => {
    if (chartInstance) {
      Object.keys(yearLevelColors).forEach((yearLevel) => {
        const series = chartInstance.seriesMap.get(yearLevel);
        if (series) {
          if (visibleYearLevels.has(yearLevel)) {
            series.show();
          } else {
            series.hide();
          }
        }
      });
    }
  }, [visibleYearLevels, chartInstance, yearLevelColors]);

  const handleAllToggle = () => {
    const allYearLevels = Object.keys(yearLevelColors);
    const allSelected = allYearLevels.every((yearLevel) => visibleYearLevels.has(yearLevel));

    if (allSelected) {
      // If all are selected, deselect all
      setVisibleYearLevels(new Set());
    } else {
      // If not all are selected, select all
      setVisibleYearLevels(new Set(allYearLevels));
    }
  };

  if (!data || Object.keys(data)?.length === 0) {
    return (
      <Box>
        <Typography variant='h6' gutterBottom>
          Number of responses over time
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
          This chart shows the cumulative response count by year level over time.
        </Typography>

        <Box display='flex' gap={2}>
          <Box flex={1}>
            <NoResponsesAvailableYet data={data} onRefresh={onRefresh} />
          </Box>

          <Paper sx={{ p: 2, minWidth: 200, backgroundColor: '#f9f9f9', opacity: 0.6 }}>
            <Box sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', fontSize: 14, mb: 1 }}>
                Color Reference
              </Typography>
              <Typography variant='body2' sx={{ color: '#757575', fontStyle: 'italic' }}>
                Will show year level colors when data is available
              </Typography>
            </Box>

            <Box sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', fontSize: 14, mb: 1 }}>
                Year Levels
              </Typography>
              <Typography variant='body2' sx={{ color: '#757575', fontStyle: 'italic' }}>
                Filter options will appear when responses are received
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <ChartHeader
        title=' Number of responses over time'
        description=' This chart shows the cumulative response count by year level over time.'
        showRefreshButton={data && Object.keys(data).length > 0}
        handleManualRefresh={handleManualRefresh}
        isRefreshing={isManualRefreshing}
      />

      <Box display='flex' gap={2}>
        <Box flex={1}>
          <Box ref={chartRef} style={{ width: '100%', height: 'calc(100vh - 380px)' }} />
        </Box>

        <Paper sx={{ p: 2, minWidth: 200, backgroundColor: '#f9f9f9' }}>
          <Box sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', fontSize: 14, mb: 1 }}>
              Color Reference
            </Typography>
            {Object.keys(yearLevelColors).map((yearLevel) => (
              <Box key={yearLevel} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    backgroundColor: yearLevelColors[yearLevel],
                    mr: 1,
                    borderRadius: 0.5
                  }}
                />
                <Typography variant='body1' sx={{ fontSize: 14 }}>
                  {yearLevel}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {/* Second section - Interactive Checkboxes */}
            <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 'bold', fontSize: 14, mb: 1 }}>
              Year Levels
            </Typography>
            <FormGroup sx={{ pl: 1 }}>
              {/* Add "All" checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Object.keys(yearLevelColors).every((yearLevel) =>
                      visibleYearLevels.has(yearLevel)
                    )}
                    indeterminate={
                      Object.keys(yearLevelColors).some((yearLevel) => visibleYearLevels.has(yearLevel)) &&
                      !Object.keys(yearLevelColors).every((yearLevel) => visibleYearLevels.has(yearLevel))
                    }
                    onChange={handleAllToggle}
                    size='medium'
                    color='default'
                  />
                }
                label='(All)'
              />

              {/* Dynamic year level checkboxes */}
              {Object.keys(yearLevelColors).map((yearLevel) => (
                <FormControlLabel
                  key={yearLevel}
                  control={
                    <Checkbox
                      checked={visibleYearLevels.has(yearLevel)}
                      onChange={() => handleYearLevelToggle(yearLevel)}
                      size='medium'
                      color='default'
                    />
                  }
                  label={yearLevel}
                  sx={{
                    mb: 0.5
                  }}
                />
              ))}
            </FormGroup>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CompletionOverTimeChart;
